/**
 * Top-level error boundary.
 *
 * The failure this exists for is a misconfigured deploy: a missing or wrong
 * `VITE_FIREBASE_*` value throws while the auth module initialises, and without a
 * boundary the person gets a blank page and a console message. Naming the likely
 * cause turns a support ticket into a one-line fix.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react'

interface State {
  error: Error | null
}

export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  override state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // No error-reporting service is wired up yet; the console is the honest place
    // for this until one is.
    console.error('Unhandled error in the portal', error, info.componentStack)
  }

  override render(): ReactNode {
    const { error } = this.state
    if (!error) return this.props.children

    const looksLikeConfig = /VITE_|firebase|api-key/i.test(error.message)

    return (
      <main id="main"
        className="shell"
        style={{ display: 'grid', placeItems: 'center', minHeight: '100vh' }}
      >
        <div style={{ width: '100%', maxWidth: 560 }}>
          <div className="panel panel--coral panel--flush">
            <div className="panel__head">
              <div className="label">
                <span className="label__index">§!!</span>
                <span>Something broke</span>
              </div>
            </div>

            <div className="panel__body stack" style={{ gap: 'var(--s4)' }}>
              <p style={{ margin: 0 }}>
                The portal failed to start. Your account, license and local data are
                unaffected — this is a problem with this page.
              </p>

              {looksLikeConfig && (
                <div className="notice notice--warn">
                  <span>
                    This looks like a configuration problem. Check that{' '}
                    <code>.env.local</code> contains the Firebase web config from your
                    project — see <code>.env.example</code>.
                  </span>
                </div>
              )}

              <pre
                className="mono-note"
                style={{
                  margin: 0,
                  padding: 'var(--s3)',
                  background: 'var(--ink)',
                  border: '1px solid var(--rule)',
                  borderRadius: 'var(--radius)',
                  overflowX: 'auto',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {error.message}
              </pre>

              <div className="row">
                <button className="btn btn--primary" onClick={() => window.location.reload()}>
                  Reload
                </button>
                <a className="btn btn--ghost" href="/">
                  Home
                </a>
              </div>

              <p className="mono-note" style={{ margin: 0 }}>
                You can still manage your license from the terminal:{' '}
                <code>chi auth status</code>, <code>chi auth refresh</code>.
              </p>
            </div>
          </div>
        </div>
      </main>
    )
  }
}
